import { useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { PencilIcon } from '@heroicons/react/24/outline';
import { IssuingBodyCombobox, StateCombobox } from '@/components/shared';
import { genderOptions, raceOptions } from '@/data/commonDataOptions';
import {
  ESCOLARIDADE_OPCOES,
  ESCOLARIDADE_STATUS_OPCOES,
  SEXO_OPCOES,
  escolaridadeTemCurso,
} from '../../../models/candidato.opcoes';
import {
  CASADO_OU_UNIAO_ESTAVEL,
  INPUT_CLASS,
  LABEL_CLASS,
  RespostasEtapa2Estado,
  emailValido,
  telefoneValido,
} from '../candidatoFormExternoEtapa2Page.definitions';
import CampoSimNao from './CampoSimNao';

interface EtapaDadosComplementaresProps {
  respostas: RespostasEtapa2Estado;
  onChange: <K extends keyof RespostasEtapa2Estado>(campo: K, valor: RespostasEtapa2Estado[K]) => void;
}

export default function EtapaDadosComplementares({ respostas, onChange }: EtapaDadosComplementaresProps) {
  const [editandoEmail, setEditandoEmail] = useState(false);

  return (
    <>
      <h2 className="mb-6 text-2xl font-bold text-gray-800">Sobre Você</h2>
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLASS}>Com qual gênero você se identifica? *</label>
          <select
            className={INPUT_CLASS}
            value={respostas.genero ?? ''}
            onChange={(e) => onChange('genero', e.target.value)}
          >
            <option value="">Selecione</option>
            {genderOptions.map((opcao) => (
              <option key={opcao}>{opcao}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL_CLASS}>Como você se declara (raça/cor)? *</label>
          <select
            className={INPUT_CLASS}
            value={respostas.raca ?? ''}
            onChange={(e) => onChange('raca', e.target.value)}
          >
            <option value="">Selecione</option>
            {raceOptions.map((opcao) => (
              <option key={opcao}>{opcao}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL_CLASS}>Sexo (conforme documento) *</label>
          <select
            className={INPUT_CLASS}
            value={respostas.sexo ?? ''}
            onChange={(e) => onChange('sexo', e.target.value)}
          >
            <option value="">Selecione</option>
            {SEXO_OPCOES.map((opcao) => (
              <option key={opcao}>{opcao}</option>
            ))}
          </select>
        </div>
        {/* Na Etapa 1 fica só o número do RG. */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Órgão emissor do RG *</label>
            <IssuingBodyCombobox
              selectedValue={respostas.orgaoEmissorRg ?? null}
              onChange={(valor) => onChange('orgaoEmissorRg', valor ?? '')}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>UF do órgão emissor *</label>
            <StateCombobox
              selectedValue={respostas.orgaoEmissorRgUf ?? null}
              onChange={(valor) => onChange('orgaoEmissorRgUf', valor ?? '')}
            />
          </div>
        </div>
        <div>
          <label className={LABEL_CLASS}>Data de emissão do RG *</label>
          <input
            type="date"
            className={INPUT_CLASS}
            value={respostas.dataEmissaoRg ?? ''}
            onChange={(e) => onChange('dataEmissaoRg', e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Qual sobrenome você gostaria que fosse usado no seu e-mail da Raro? *</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={respostas.sobrenomeEmail ?? ''}
            onChange={(e) => onChange('sobrenomeEmail', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Nacionalidade *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.nacionalidade ?? ''}
              onChange={(e) => onChange('nacionalidade', e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Estado civil *</label>
            <select
              className={INPUT_CLASS}
              value={respostas.estadoCivil ?? ''}
              onChange={(e) => onChange('estadoCivil', e.target.value)}
            >
              <option value="">Selecione</option>
              <option>Solteiro(a)</option>
              <option>Casado(a)</option>
              <option>Divorciado(a)</option>
              <option>Viúvo(a)</option>
              <option>União Estável</option>
            </select>
          </div>
        </div>
        {CASADO_OU_UNIAO_ESTAVEL.includes(respostas.estadoCivil ?? '') && (
          <div>
            <label className={LABEL_CLASS}>Nome do cônjuge *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.nomeConjuge ?? ''}
              onChange={(e) => onChange('nomeConjuge', e.target.value)}
            />
          </div>
        )}
        <CampoSimNao
          label="Possui alguma deficiência?"
          valor={respostas.possuiDeficiencia}
          onChange={(valor) => onChange('possuiDeficiencia', valor)}
        />
        {respostas.possuiDeficiencia && (
          <div>
            <label className={LABEL_CLASS}>Qual e o CID? *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.deficienciaCid ?? ''}
              onChange={(e) => onChange('deficienciaCid', e.target.value)}
            />
          </div>
        )}
        {/* Mesmas opções do perfil (Formação) — o status não existe lá, fica só na resposta. */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Escolaridade *</label>
            <select
              className={INPUT_CLASS}
              value={respostas.escolaridade ?? ''}
              onChange={(e) => onChange('escolaridade', e.target.value)}
            >
              <option value="">Selecione</option>
              {ESCOLARIDADE_OPCOES.map((opcao) => (
                <option key={opcao}>{opcao}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS}>Situação *</label>
            <select
              className={INPUT_CLASS}
              value={respostas.escolaridadeStatus ?? ''}
              onChange={(e) => onChange('escolaridadeStatus', e.target.value)}
            >
              <option value="">Selecione</option>
              {ESCOLARIDADE_STATUS_OPCOES.map((opcao) => (
                <option key={opcao}>{opcao}</option>
              ))}
            </select>
          </div>
        </div>
        {escolaridadeTemCurso(respostas.escolaridade) && (
          <div>
            <label className={LABEL_CLASS}>Qual o curso? *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.escolaridadeCurso ?? ''}
              onChange={(e) => onChange('escolaridadeCurso', e.target.value)}
            />
          </div>
        )}
        <div>
          <label className={LABEL_CLASS}>Telefone para contato *</label>
          <PhoneInput
            value={respostas.telefone ?? ''}
            onChange={(valor) => onChange('telefone', valor)}
            defaultCountry="br"
            inputClassName={INPUT_CLASS}
          />
          {respostas.telefone && !telefoneValido(respostas.telefone) && (
            <p className="mt-1 text-sm text-red-600">Informe o telefone completo, com DDD e número.</p>
          )}
        </div>
        <div>
          <label className={LABEL_CLASS}>E-mail pessoal *</label>
          <p className="mb-1 text-xs text-gray-500">
            Este é o e-mail que a Raro já tem cadastrado pra você. Se não for o certo, clique no lápis pra corrigir.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="email"
              className={`${INPUT_CLASS} ${!editandoEmail ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''}`}
              value={respostas.emailPessoal ?? ''}
              disabled={!editandoEmail}
              onChange={(e) => onChange('emailPessoal', e.target.value)}
            />
            <button
              type="button"
              onClick={() => setEditandoEmail((atual) => !atual)}
              className="flex-shrink-0 rounded-xl border-2 border-gray-100 p-3 text-gray-500 hover:border-indigo-200 hover:text-indigo-600"
              title="Editar e-mail"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>
          {respostas.emailPessoal && !emailValido(respostas.emailPessoal) && (
            <p className="mt-1 text-sm text-red-600">E-mail inválido.</p>
          )}
        </div>
      </div>
    </>
  );
}
